/* eslint-disable react-refresh/only-export-components -- hook + provider is standard React context pattern */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { MessageCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { ChatPanelContextType } from "./types";

const ChatPanelContext = createContext<ChatPanelContextType | null>(null);

function FAB({ onClick }: { onClick: () => void }) {
  return (
    <div className="fixed bottom-6 right-6 z-40">
      {/* Ambient pulse ring */}
      <motion.div
        className="absolute inset-0 rounded-full bg-accent/20"
        animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ duration: 2.5, ease: "easeInOut", repeat: Infinity }}
      />
      <motion.button
        onClick={onClick}
        type="button"
        className="relative h-14 w-14 rounded-full bg-accent text-white shadow-lg hover:bg-accent/90 hover:scale-105 transition-all flex items-center justify-center cursor-pointer"
        aria-label="Open AI assistant"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <MessageCircle size={24} />
      </motion.button>
    </div>
  );
}

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const openPanel = useCallback(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    // Return focus to previously focused element
    requestAnimationFrame(() => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    });
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
      } else {
        requestAnimationFrame(() => {
          previousFocusRef.current?.focus();
          previousFocusRef.current = null;
        });
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "j") {
        e.preventDefault();
        togglePanel();
      }
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closePanel();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [togglePanel, closePanel, isOpen]);

  return (
    <ChatPanelContext.Provider
      value={{ isOpen, openPanel, closePanel, togglePanel }}
    >
      {children}
      <AnimatePresence>
        {!isOpen && <FAB onClick={openPanel} />}
      </AnimatePresence>
    </ChatPanelContext.Provider>
  );
}

export function useChatPanel(): ChatPanelContextType {
  const context = use(ChatPanelContext);
  if (!context) {
    throw new Error("useChatPanel must be used within a ChatPanelProvider");
  }
  return context;
}
